import { useEffect, useMemo } from 'react';

import { PLAYBACK_RATE_MAX, PLAYBACK_RATE_MIN } from 'constants/params';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { selectClipData } from 'components/app/_helpers/_app.helper';
import { BackupHandlers } from 'components/app/editor/_mediator/backup/backup.slice';
import { EMPTY_CELL, Clip } from 'components/app/editor/_mediator/editor.slice';
import {
  SB_CLIP_PLAYBACK_PARAMS,
  SbClipPlaybackMap,
  StoryBoardTracks,
  setErrorRate,
  setSbClipPlaybackMap,
} from 'components/app/storyboard/_mediator/sb.slice';
import { isImage } from 'components/app/storyboard/sb-player/_mediator/sb-player.helper';
import { getPlaybackParams, getConfigRate } from './clip-playback.helper';

interface Props {
  clipId: CellClip;
  index: number;
  track: StoryBoardTracks;
  clipData?: Clip;
}

export const useClipPlaybackSb = ({ clipId, index, clipData, track }: Props) => {
  const { backups } = useAppSelector((state) => state.backup);
  const { cells, clipsMap, linkedClips, storyBoard, storyConfig } = useAppSelector((state) => state.editor);
  const { sbClipPlaybackMap } = useAppSelector((state) => state.sb);
  const dispatch = useAppDispatch();

  const configRate = useMemo(() => {
    if (!clipData) return 1;
    return getConfigRate(clipData, storyConfig);
  }, [clipData, storyConfig]);

  const isLinked = () => {
    /* USE_CASE: changing the timestamp for the linked group */
    const backup = backups[backups.length - 1];
    return !!(
      backup &&
      sbClipPlaybackMap &&
      clipData?.clipGroupId &&
      backup.handler === BackupHandlers.handleClips &&
      backup.data.linkedClips
    );
  };

  const getParams = (payload: Index[]) => {
    if (!clipsMap || !cells) return {};
    return payload.reduce((acc, index) => {
      const clipId = storyBoard[track][index];
      if (!clipId) return acc;
      acc[clipId] = isImage(clipId)
        ? { ...SB_CLIP_PLAYBACK_PARAMS, cellIndex: index }
        : (acc[clipId] = getPlaybackParams({
            cellDuration: cells.duration[index],
            clipData: selectClipData(clipId, clipsMap),
            configRate: getConfigRate(selectClipData(clipId, clipsMap), storyConfig),
            linkedClips,
            clipsMap,
            index,
          }));
      return acc;
    }, {} as SbClipPlaybackMap);
  };

  const validateParams = (params: SbClipPlaybackMap) => {
    Object.keys(params!).forEach((clipId) => {
      const data = params[clipId];
      const rate = data.rate;
      if (rate > PLAYBACK_RATE_MAX) {
        data.rate = PLAYBACK_RATE_MAX;
        dispatch(setErrorRate({ clipId, value: 'max' }));
      } else if (rate < PLAYBACK_RATE_MIN) {
        data.rate = PLAYBACK_RATE_MIN;
        dispatch(setErrorRate({ clipId, value: 'min' }));
      } else dispatch(setErrorRate({ clipId }));
    });
    return params;
  };

  useEffect(() => {
    if (clipId === undefined || clipId === EMPTY_CELL || !cells) return;
    const indexes = isLinked() ? [index - 1, index, index + 1] : [index];
    const params = validateParams(getParams(indexes));
    dispatch(setSbClipPlaybackMap(params));
  }, [clipId, cells?.duration[index], configRate, clipData && linkedClips[clipData.clipGroupId]]);
};
